import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { useAuth } from '../../contexts/AuthContext';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { recordManualPayment } from '../../utils/recordManualPayment';
import { logAuditEntry } from '../../utils/auditLog';
import { formatClubDateTime } from '../../utils/clubTime';

const client = generateClient({ authMode: 'userPool' });

interface PaymentRecordSummary {
  id: string;
  amountCents: number;
  currency: string;
  status: 'COMPLETED' | 'FAILED';
  entityId: string;
  timestamp: string;
}

// Matches the shape check already used in MemberApplications.tsx for the same model —
// kept local rather than shared, since PaymentRecord is returned as `any` from the
// generated client in both files and each caller validates its own subset of fields.
const isPaymentRecordSummary = (record: unknown): record is PaymentRecordSummary => {
  if (!record || typeof record !== 'object') return false;
  const candidate = record as Partial<PaymentRecordSummary>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.entityId === 'string' &&
    typeof candidate.amountCents === 'number' &&
    Number.isFinite(candidate.amountCents) &&
    typeof candidate.currency === 'string' &&
    (candidate.status === 'COMPLETED' || candidate.status === 'FAILED') &&
    typeof candidate.timestamp === 'string' &&
    Number.isFinite(new Date(candidate.timestamp).getTime())
  );
};

interface RegistrationDetailViewProps {
  registrationId: string;
}

const RegistrationDetailView: React.FC<RegistrationDetailViewProps> = ({ registrationId }) => {
  const { user, userId } = useAuth();
  const [registration, setRegistration] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [feeAmount, setFeeAmount] = useState<number>(0);
  const [feeAdjustmentReason, setFeeAdjustmentReason] = useState<string>('');
  const [isEditingFee, setIsEditingFee] = useState(false);
  const [originalFeeAmount, setOriginalFeeAmount] = useState<number>(0);
  const [feeValidationError, setFeeValidationError] = useState<string>('');
  // Combined registration: the linked membership component (shown for reconciliation).
  const [linkedMember, setLinkedMember] = useState<any>(null);
  // Square verification + full attempt history (incl. failures) for this registration,
  // mirroring the same fields already shown on the Member Applications review — see
  // BACKLOG for the parity rationale. Loaded alongside the registration, not per-render.
  const [paymentRecords, setPaymentRecords] = useState<PaymentRecordSummary[]>([]);

  // #163: straight to this registration's competition (its Manage
  // Check-Ins dashboard), whatever page the admin came from.
  const navigateToCompetition = () => {
    if (!registration?.eventId) return;
    window.history.pushState({}, '', `/admin/competition-day/${registration.eventId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const navigateBack = () => {
    window.history.back();
  };

  useEffect(() => {
    loadRegistration();
  }, [registrationId]);

  const loadRegistration = async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await client.models.CompetitionDayRegistration.get({ id: registrationId });

      if (!data) {
        setError('Registration not found.');
        setLoading(false);
        return;
      }

      setRegistration(data);
      setFeeAmount(data.feeAmount || 0);
      setOriginalFeeAmount(data.feeAmount || 0);
      setFeeAdjustmentReason(data.feeAdjustmentReason || '');

      // If this entry is linked to a member (e.g. combined registration), load the
      // membership component for a grouped, reconcilable view.
      if (data.memberId) {
        try {
          const { data: m } = await client.models.Member.get({ id: data.memberId });
          setLinkedMember(m || null);
        } catch {
          /* non-fatal — membership panel just won't show */
        }
      }

      // Payment attempt history for this exact registration, filtered by entityId
      // server-side rather than fetched globally then filtered client-side — this
      // screen only ever needs one registration's records, unlike the admin list
      // view in MemberApplications.tsx which loads all records once per page.
      // Paginated fully so a large PaymentRecord table cannot silently truncate an
      // older attempt off this registration's history.
      try {
        const records: PaymentRecordSummary[] = [];
        let nextToken: string | null | undefined;
        do {
          const page: any = await client.models.PaymentRecord.list({
            filter: { entityId: { eq: registrationId } },
            nextToken: nextToken || undefined,
          });
          records.push(...(page.data || []).filter(isPaymentRecordSummary));
          nextToken = page.nextToken;
        } while (nextToken);
        setPaymentRecords(
          records.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
        );
      } catch (err) {
        console.error('Error loading payment history (non-fatal):', err);
      }
    } catch (err) {
      console.error('Error loading registration:', err);
      setError('Failed to load registration details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFee = async () => {
    if (feeAmount < 0) {
      setError('Fee amount cannot be negative.');
      return;
    }

    // Require reason when fee changes from original
    const effectiveReason =
      feeAdjustmentReason.startsWith('Other') && !feeAdjustmentReason.startsWith('Other:')
        ? feeAdjustmentReason
        : feeAdjustmentReason;
    if (feeAmount !== originalFeeAmount && !effectiveReason) {
      setFeeValidationError('Please select an adjustment reason before saving.');
      return;
    }

    setError('');
    setSuccess('');
    setFeeValidationError('');
    setLoading(true);

    try {
      const { data } = await client.models.CompetitionDayRegistration.update({
        id: registrationId,
        feeAmount: feeAmount,
        feeAdjustmentReason: feeAdjustmentReason || null,
      });

      setRegistration(data);
      setIsEditingFee(false);
      setOriginalFeeAmount(feeAmount);
      setSuccess('Fee amount updated successfully.');
      logAuditEntry(user?.email || '', userId || '', {
        actionType: 'FEE_ADJUSTED',
        targetEntityType: 'CompetitionDayRegistration',
        targetEntityId: registrationId,
        description: `Comp-day fee adjusted to $${feeAmount} for ${registration.participantName} (${registration.participantEmail})`,
        metadata: {
          previousAmount: originalFeeAmount,
          newAmount: feeAmount,
          reason: feeAdjustmentReason || null,
        },
      });
    } catch (err) {
      console.error('Error updating fee:', err);
      setError('Failed to update fee amount. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaymentReceived = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // #163 follow-up: shared with the Competition Day Dashboard list.
      const data = await recordManualPayment(client, registration, { email: user?.email || '', userId: userId || '' });
      setRegistration(data);
      setSuccess('Payment marked as received.');
    } catch (err) {
      console.error('Error updating payment status:', err);
      setError('Failed to update payment status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFinalizeRegistration = async () => {
    if (registration.paymentStatus !== 'RECEIVED') {
      setError('Cannot finalise registration. Payment must be received first.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // #163: through the server rule (payment must be RECEIVED); the Lambda
      // writes the REGISTRATION_FINALIZED audit entry.
      const res = await (client.mutations as any).finalizeCompetitionDayRegistration({ registrationId });
      if (res?.errors?.length) throw new Error(res.errors.map((e: any) => e.message).join('; '));
      const body = parseLambdaResponse<{ success: boolean; message?: string; registration?: any }>(res?.data);
      if (!body?.success) {
        setError(body?.message || 'Failed to finalise registration. Please try again.');
        return;
      }
      setRegistration({ ...registration, ...body.registration });
      setSuccess('Registration finalised successfully.');
    } catch (err) {
      console.error('Error finalising registration:', err);
      setError('Failed to finalise registration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (timestamp: string | null | undefined) => {
    if (!timestamp) return 'N/A';
    return formatClubDateTime(timestamp, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading && !registration) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading registration details...</div>
      </div>
    );
  }

  if (error && !registration) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
    );
  }

  if (!registration) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Registration Details
            </h1>
            <p className="text-sm text-gray-600">ID: {registration.id}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            {registration.eventId && (
              <button
                onClick={navigateToCompetition}
                className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back to competition
              </button>
            )}
            <button
              onClick={navigateBack}
              className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex flex-wrap gap-3">
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              registration.paymentStatus === 'RECEIVED'
                ? 'bg-green-100 text-green-800'
                : 'bg-yellow-100 text-yellow-800'
            }`}
          >
            Payment: {registration.paymentStatus}
          </span>
          <span
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              registration.registrationStatus === 'FINALIZED'
                ? 'bg-blue-100 text-blue-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            Status: {registration.registrationStatus}
          </span>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {success}
        </div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">{error}</div>
      )}

      {/* Participant Information */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Participant Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-600">Name</label>
            <p className="text-gray-900">{registration.participantName}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Email</label>
            <p className="text-gray-900">{registration.participantEmail}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Phone</label>
            <p className="text-gray-900">{registration.participantPhone}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-600">Registration Type</label>
            <p className="text-gray-900">{registration.registrationType?.replace('_', ' ')}</p>
          </div>
          {registration.memberNumber && (
            <div>
              <label className="text-sm font-medium text-gray-600">Member Number</label>
              <p className="text-gray-900">{registration.memberNumber}</p>
            </div>
          )}
          {registration.familyMemberName && (
            <div>
              <label className="text-sm font-medium text-gray-600">Family Member</label>
              <p className="text-gray-900">{registration.familyMemberName}</p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-600">Check-In Time</label>
            <p className="text-gray-900">{formatDateTime(registration.checkInTimestamp)}</p>
          </div>
        </div>
      </div>

      {/* Payment Information */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Information</h2>

        <div className="space-y-4">
          {/* Fee Amount */}
          <div>
            <label className="text-sm font-medium text-gray-600 block mb-2">Fee Amount</label>
            {isEditingFee ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="number"
                    value={feeAmount}
                    onChange={(e) => setFeeAmount(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Fee amount"
                  />
                  <button
                    onClick={handleUpdateFee}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingFee(false);
                      setFeeAmount(registration.feeAmount || 0);
                      setFeeAdjustmentReason(registration.feeAdjustmentReason || '');
                      setFeeValidationError('');
                    }}
                    className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600 block mb-1">
                    Adjustment Reason
                  </label>
                  <select
                    value={feeAdjustmentReason.startsWith('Other') ? 'Other' : feeAdjustmentReason}
                    onChange={(e) => {
                      setFeeAdjustmentReason(e.target.value);
                      setFeeValidationError('');
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No adjustment</option>
                    <option value="Committee member">Committee member</option>
                    <option value="Volunteer discount">Volunteer discount</option>
                    <option value="Family discount">Family discount</option>
                    <option value="Sponsor discount">Sponsor discount</option>
                    <option value="Financial hardship">Financial hardship</option>
                    <option value="Promotional offer">Promotional offer</option>
                    <option value="Other">Other</option>
                  </select>
                  {feeAdjustmentReason.startsWith('Other') && (
                    <input
                      type="text"
                      placeholder="Enter reason..."
                      value={
                        feeAdjustmentReason.startsWith('Other: ')
                          ? feeAdjustmentReason.slice(7)
                          : ''
                      }
                      onChange={(e) => {
                        const text = e.target.value.trim();
                        setFeeAdjustmentReason(text ? `Other: ${text}` : 'Other');
                      }}
                      className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  {feeValidationError && (
                    <p className="mt-1 text-sm text-red-600">{feeValidationError}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <p className="text-2xl font-bold text-gray-900">${registration.feeAmount}</p>
                  {registration.registrationStatus !== 'FINALIZED' &&
                    !registration.squarePaymentId && (
                      <button
                        onClick={() => setIsEditingFee(true)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                    )}
                </div>
                {registration.feeAdjustmentReason && (
                  <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
                    <strong>Reason:</strong> {registration.feeAdjustmentReason}
                  </div>
                )}
                {/* Fee editing is locked once Square has charged a specific amount —
                    changing it afterward would make the record disagree with what was
                    actually charged, the same class of drift MemberApplications.tsx
                    prevents by disabling its payment toggle once squarePaymentId is set. */}
                {registration.squarePaymentId &&
                  registration.registrationStatus !== 'FINALIZED' && (
                    <p className="text-xs text-gray-500">
                      Fee locked — this amount was charged via Square and cannot be edited here.
                      Process any refund via the Square Dashboard first.
                    </p>
                  )}
              </div>
            )}
          </div>

          {/* Payment Status */}
          <div>
            <label className="text-sm font-medium text-gray-600 block mb-2">Payment Status</label>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  registration.paymentStatus === 'RECEIVED'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}
              >
                {registration.paymentStatus}
              </span>
              {registration.paymentStatus === 'PENDING' &&
                registration.registrationStatus !== 'FINALIZED' && (
                  <button
                    onClick={handleMarkPaymentReceived}
                    disabled={loading}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300"
                  >
                    Mark as Received
                  </button>
                )}
            </div>
          </div>

          {/* Square verification — same visual pattern as MemberApplications.tsx's
              "Payment verified by Square" panel, so an admin sees the same signal in
              both places rather than having to remember comp-day works differently. */}
          {registration.squarePaymentId ? (
            <div className="bg-green-50 border border-green-300 rounded-lg p-3">
              <p className="text-sm font-semibold text-green-800">✓ Payment verified by Square</p>
              <p className="text-xs text-green-700 mt-1 font-mono">
                Square ID: {registration.squarePaymentId}
              </p>
            </div>
          ) : (
            registration.paymentStatus === 'RECEIVED' && (
              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                <p className="text-sm font-semibold text-amber-800">
                  ⚠️ Marked as paid manually — not verified by Square.
                </p>
              </div>
            )
          )}

          {registration.paymentTimestamp && (
            <div>
              <label className="text-sm font-medium text-gray-600">Payment Received At</label>
              <p className="text-gray-900">{formatDateTime(registration.paymentTimestamp)}</p>
            </div>
          )}

          {/* Payment History — mirrors MemberApplications.tsx: every PaymentRecord for
              this registration, including failed attempts, most recent first. */}
          {paymentRecords.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-2">
                Payment History ({paymentRecords.length})
              </label>
              <div className="space-y-2">
                {paymentRecords.map((r) => (
                  <div
                    key={r.id}
                    className={`flex items-center justify-between p-3 rounded-lg border text-sm ${
                      r.status === 'COMPLETED'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div>
                      <span
                        className={`font-semibold ${r.status === 'COMPLETED' ? 'text-green-800' : 'text-red-800'}`}
                      >
                        {r.status === 'COMPLETED' ? '✓ Completed' : '✗ Failed'}
                      </span>
                      <span className="text-gray-600 ml-2">
                        ${(r.amountCents / 100).toFixed(2)} {r.currency}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">{formatDateTime(r.timestamp)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Membership component (combined registration) — separate component, shown for reconciliation */}
      {linkedMember && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Membership Component</h2>
          <p className="text-sm text-gray-500 mb-4">
            This entry is linked to a club membership. The membership fee is a separate component —
            manage and mark it paid in Member Management. (No partial payments; adjust the amount to
            vary a component.)
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Membership Type</label>
              <p className="text-gray-900">{linkedMember.membershipType || '—'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Membership Fee</label>
              <p className="text-gray-900">${linkedMember.amountPaid ?? 0}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Membership Payment</label>
              <div>
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    linkedMember.paymentReceived
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {linkedMember.paymentReceived ? 'RECEIVED' : 'PENDING'}
                </span>
              </div>
            </div>
          </div>
          {linkedMember.paymentAdjustmentReason && (
            <div className="mt-3 text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
              <strong>Membership adjustment:</strong> {linkedMember.paymentAdjustmentReason}
            </div>
          )}
        </div>
      )}

      {/* Agreement Acceptance */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Agreement Acceptance</h2>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg gap-2">
            <span className="text-gray-900">Club Rules</span>
            <div className="text-left sm:text-right">
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  registration.clubRulesAccepted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {registration.clubRulesAccepted ? 'Accepted' : 'Not Accepted'}
              </span>
              {registration.clubRulesAcceptedTimestamp && (
                <p className="text-xs text-gray-600 mt-1">
                  {formatDateTime(registration.clubRulesAcceptedTimestamp)}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg gap-2">
            <span className="text-gray-900">Code of Conduct</span>
            <div className="text-left sm:text-right">
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  registration.codeOfConductAccepted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {registration.codeOfConductAccepted ? 'Accepted' : 'Not Accepted'}
              </span>
              {registration.codeOfConductAcceptedTimestamp && (
                <p className="text-xs text-gray-600 mt-1">
                  {formatDateTime(registration.codeOfConductAcceptedTimestamp)}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 bg-gray-50 rounded-lg gap-2">
            <span className="text-gray-900">Waiver & Release</span>
            <div className="text-left sm:text-right">
              <span
                className={`px-2 py-1 rounded text-xs font-medium ${
                  registration.waiverAccepted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}
              >
                {registration.waiverAccepted ? 'Accepted' : 'Not Accepted'}
              </span>
              {registration.waiverAcceptedTimestamp && (
                <p className="text-xs text-gray-600 mt-1">
                  {formatDateTime(registration.waiverAcceptedTimestamp)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Finalise Registration */}
      {registration.registrationStatus !== 'FINALIZED' && (
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Finalise Registration</h2>
          <p className="text-gray-600 mb-4">
            Once payment has been received, you can finalise this registration. This action cannot
            be undone.
          </p>
          {registration.paymentStatus !== 'RECEIVED' && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm mb-4">
              Payment must be marked as received before finalising the registration.
            </div>
          )}
          <button
            onClick={handleFinalizeRegistration}
            disabled={loading || registration.paymentStatus !== 'RECEIVED'}
            className="w-full sm:w-auto px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Finalise Registration
          </button>
        </div>
      )}
    </div>
  );
};

export default RegistrationDetailView;
